import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "react-hot-toast";
import mentorshipService from "@/services/mentorshipService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, FileText, Users } from "lucide-react";

function AddStudentsCSVModal({ open, onOpenChange, courseName, onSuccess }) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      handleReset();
      setTimeout(() => {
        const firstInput = document.querySelector('#csv-file');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } else {
      setIsUploading(false);
      setError(null);
      setProgress(0);
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsUploading(false);
      setError(null);
      setProgress(0);
    };
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
      setError("Please select a valid CSV file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    if (!courseName) {
      setError("Course name is required");
      return;
    }

    const uploadToastId = toast.loading("Uploading students...");
    try {
      setIsUploading(true);
      setError(null);
      setProgress(10);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("courseName", courseName);

      setProgress(30);
      const response = await mentorshipService.uploadStudentsCSV(formData);
      setProgress(100);

      toast.success(`Successfully added ${response.totalProcessed || 0} students!`, { id: uploadToastId });
      
      if (onSuccess) {
        onSuccess(response);
      }

      handleReset();
      onOpenChange(false);
    } catch (err) {
      console.error("Error uploading CSV:", err);
      const errorMessage = err.response?.data?.error || "Failed to upload CSV. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, { id: uploadToastId });
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleReset = () => {
    setFile(null);
    setError(null);
    setProgress(0);
    // Reset file input
    const fileInput = document.querySelector('#csv-file');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleModalClose = (newOpen) => {
    if (!newOpen && isUploading) {
      return; // Prevent closing while uploading
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Add Students via CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file with student information. Students will be automatically assigned to available batches.
            <br />
            <strong>Required columns:</strong> name, email, phone
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csv-file">CSV File</Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
              required
            />
            <p className="text-sm text-gray-500">
              File should contain: name, email, phone columns
            </p>
          </div>

          <div className="space-y-2">
            <Label>Course</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{courseName}</span>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Uploading students...</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!file || isUploading}
              className="flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  Add Students
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

AddStudentsCSVModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  courseName: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

export default AddStudentsCSVModal;

// BatchDeleteStudentsCSVModal: Modal for batch deleting students via CSV
export function BatchDeleteStudentsCSVModal({ open, onOpenChange, courseName, onSuccess }) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      handleReset();
      setTimeout(() => {
        const firstInput = document.querySelector('#csv-delete-file');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } else {
      setIsUploading(false);
      setError(null);
      setProgress(0);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      setIsUploading(false);
      setError(null);
      setProgress(0);
    };
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
      setError("Please select a valid CSV file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a CSV file");
      return;
    }
    if (!courseName) {
      setError("Course name is required");
      return;
    }
    const uploadToastId = toast.loading("Deleting students...");
    try {
      setIsUploading(true);
      setError(null);
      setProgress(10);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("courseName", courseName);
      setProgress(30);
      const response = await mentorshipService.batchDeleteStudentsCSV(formData);
      setProgress(100);
      toast.success(`Deleted: ${response.deletedCount || 0}, Failed: ${response.failedCount || 0}`, { id: uploadToastId });
      if (onSuccess) {
        onSuccess(response);
      }
      handleReset();
      onOpenChange(false);
    } catch (err) {
      console.error("Error deleting students:", err);
      const errorMessage = err.response?.data?.error || "Failed to delete students. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, { id: uploadToastId });
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleReset = () => {
    setFile(null);
    setError(null);
    setProgress(0);
    const fileInput = document.querySelector('#csv-delete-file');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleModalClose = (newOpen) => {
    if (!newOpen && isUploading) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Delete Students via CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file with student information to delete them in bulk.<br />
            <strong>Required columns:</strong> courseName, batchId, phone
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csv-delete-file">CSV File</Label>
            <Input
              id="csv-delete-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
              required
            />
            <p className="text-sm text-gray-500">
              File should contain: name, email, phone columns
            </p>
          </div>
          <div className="space-y-2">
            <Label>Course</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{courseName}</span>
            </div>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Deleting students...</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!file || isUploading}
              className="flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" /> Delete Students
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

BatchDeleteStudentsCSVModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  courseName: PropTypes.string,
  onSuccess: PropTypes.func
}; 